import { Howl, Howler, HowlOptions } from 'howler';

/**
 * 音频管理器 - 使用 Howler.js 实现真正的音量放大
 */
export class AudioManager {
  private static instance: AudioManager;
  private sounds: Map<string, Howl> = new Map();
  private currentPlayingSounds: Set<string> = new Set();

  private constructor() {
    // 设置全局音量
    Howler.volume(1.0);
  }

  public static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  /**
   * 创建或获取音频实例
   * @param id 音频唯一标识
   * @param src 音频源URL
   * @param options 音频选项
   */
  public createSound(id: string, src: string, options: Partial<HowlOptions> = {}): Howl {
    // 如果已存在，先销毁
    if (this.sounds.has(id)) {
      this.sounds.get(id)?.unload();
    }

    const sound = new Howl({
      src: [src],
      volume: 1.0, // 默认音量，实际音量通过 gain 控制
      preload: true,
      html5: true, // 使用 HTML5 模式以支持真正的变速不变调
      ...options
    });

    this.sounds.set(id, sound);
    return sound;
  }

  /**
   * 播放音频并设置音量增益
   * @param id 音频标识
   * @param volumeGain 音量增益倍数 (0.5-2.0)
   * @param startTime 开始时间（秒）
   */
  public async playSound(id: string, volumeGain: number = 1.0, startTime: number = 0): Promise<void> {
    const sound = this.sounds.get(id);
    if (!sound) {
      throw new Error(`音频 ${id} 不存在`);
    }

    // 等待音频加载完成
    if (sound.state() !== 'loaded') {
      await new Promise<void>((resolve, reject) => {
        sound.once('load', () => resolve());
        sound.once('loaderror', (id, error) => reject(error));
      });
    }

    // 设置音量增益
    this.setVolumeGain(id, volumeGain);

    // 处理浏览器音频上下文暂停问题（参考测试案例）
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      await Howler.ctx.resume();
    }

    // 播放音频
    const soundId = sound.play();
    this.currentPlayingSounds.add(id);

    // 如果指定了开始时间，跳转到指定位置
    if (startTime > 0) {
      sound.seek(startTime, soundId);
    }

    // 监听播放结束事件
    sound.once('end', () => {
      this.currentPlayingSounds.delete(id);
    });

    console.log(`🔊 播放音频 ${id}，音量增益: ${volumeGain}x`);
  }

  /**
   * 设置音频的音量增益
   * @param id 音频标识
   * @param volumeGain 音量增益倍数 (0.5-2.0)
   */
  public setVolumeGain(id: string, volumeGain: number): void {
    const sound = this.sounds.get(id);
    if (!sound) {
      console.warn(`音频 ${id} 不存在`);
      return;
    }

    // 限制音量增益范围
    const clampedGain = Math.max(0.1, Math.min(3.0, volumeGain));
    
    // 使用 Howler.js 的 volume 方法设置音量
    // Howler.js 内部使用 Web Audio API 的 GainNode 实现真正的音量放大
    sound.volume(clampedGain);

    console.log(`🔊 设置音频 ${id} 音量增益为: ${clampedGain}x`);
  }

  /**
   * 停止播放音频
   * @param id 音频标识
   */
  public stopSound(id: string): void {
    const sound = this.sounds.get(id);
    if (sound) {
      // 停止所有该音频的播放实例
      sound.stop();
      this.currentPlayingSounds.delete(id);
      console.log(`🔇 停止播放音频 ${id}`);
    }
  }

  /**
   * 停止所有正在播放的音频（使用 Howler.js 全局方法）
   */
  public stopAll(): void {
    // 使用 Howler.js 的全局停止方法，停止所有音频
    try {
      Howler.stop();
    } catch (error) {
      console.warn('Howler.stop() 失败:', error);
    }
    
    // 直接操作所有 Howler.js 管理的音频实例
    this.sounds.forEach((sound, id) => {
      try {
        sound.stop();
      } catch (error) {
        console.warn(`停止音频 ${id} 失败:`, error);
      }
    });
    
    // 直接操作 DOM 中的所有 audio 元素（最激进的停止方法）
    try {
      const allAudioElements = document.querySelectorAll('audio');
      allAudioElements.forEach((audio: HTMLAudioElement) => {
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
        } catch (error) {
          // 忽略错误
        }
      });
    } catch (error) {
      console.warn('停止 DOM audio 元素失败:', error);
    }
    
    this.currentPlayingSounds.clear();
    console.log('🔇 停止所有音频播放');
  }

  /**
   * 暂停播放音频
   * @param id 音频标识
   */
  public pauseSound(id: string): void {
    const sound = this.sounds.get(id);
    if (sound) {
      sound.pause();
      console.log(`⏸️ 暂停播放音频 ${id}`);
    }
  }

  /**
   * 恢复播放音频
   * @param id 音频标识
   */
  public resumeSound(id: string): void {
    const sound = this.sounds.get(id);
    if (sound) {
      sound.play();
      console.log(`▶️ 恢复播放音频 ${id}`);
    }
  }

  /**
   * 检查音频是否正在播放
   * @param id 音频标识
   */
  public isPlaying(id: string): boolean {
    return this.currentPlayingSounds.has(id);
  }

  /**
   * 获取音频时长
   * @param id 音频标识
   */
  public getDuration(id: string): number {
    const sound = this.sounds.get(id);
    return sound ? sound.duration() : 0;
  }

  /**
   * 获取音频当前播放位置
   * @param id 音频标识
   */
  public getCurrentTime(id: string): number {
    const sound = this.sounds.get(id);
    return sound ? sound.seek() : 0;
  }

  /**
   * 设置音频播放位置
   * @param id 音频标识
   * @param time 时间位置（秒）
   */
  public setCurrentTime(id: string, time: number): void {
    const sound = this.sounds.get(id);
    if (sound) {
      sound.seek(time);
    }
  }

  /**
   * 设置音频播放速度
   * @param id 音频标识
   * @param rate 播放速度倍数 (0.5-2.0)
   */
  public setPlaybackRate(id: string, rate: number): void {
    const sound = this.sounds.get(id);
    if (!sound) {
      console.warn(`音频 ${id} 不存在`);
      return;
    }

    // 限制播放速度范围
    const clampedRate = Math.max(0.5, Math.min(2.0, rate));
    
    // 使用 Howler.js 的 rate 方法设置播放速度
    sound.rate(clampedRate);

    console.log(`🔊 设置音频 ${id} 播放速度为: ${clampedRate}x`);
  }

  /**
   * 销毁音频实例
   * @param id 音频标识
   */
  public destroySound(id: string): void {
    const sound = this.sounds.get(id);
    if (sound) {
      sound.unload();
      this.sounds.delete(id);
      this.currentPlayingSounds.delete(id);
      console.log(`🗑️ 销毁音频 ${id}`);
    }
  }

  /**
   * 销毁所有音频实例
   */
  public destroyAllSounds(): void {
    // 先使用全局停止方法停止所有音频
    try {
      Howler.stop(); // 使用 Howler.js 全局方法停止所有音频
    } catch (error) {
      console.warn('使用 Howler.stop() 停止音频时出错:', error);
    }
    
    // 然后停止并销毁所有音频实例
    this.sounds.forEach((sound, id) => {
      try {
        sound.stop(); // 先停止播放（双重保障）
        sound.unload(); // 再卸载
      } catch (error) {
        // 如果音频已经卸载，忽略错误
        console.warn(`销毁音频 ${id} 时出错:`, error);
      }
    });
    this.sounds.clear();
    this.currentPlayingSounds.clear();
    
    // 直接操作 DOM 中的所有 audio 元素（最激进的停止方法）
    try {
      const allAudioElements = document.querySelectorAll('audio');
      allAudioElements.forEach((audio: HTMLAudioElement) => {
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
        } catch (error) {
          // 忽略错误
        }
      });
    } catch (error) {
      console.warn('销毁 DOM audio 元素失败:', error);
    }
    
    console.log('🗑️ 销毁所有音频实例');
  }

  /**
   * 设置全局音量
   * @param volume 全局音量 (0.0-1.0)
   */
  public setGlobalVolume(volume: number): void {
    const clampedVolume = Math.max(0.0, Math.min(1.0, volume));
    Howler.volume(clampedVolume);
    console.log(`🔊 设置全局音量为: ${clampedVolume}`);
  }

  /**
   * 获取当前播放的音频列表
   */
  public getPlayingSounds(): string[] {
    return Array.from(this.currentPlayingSounds);
  }
}

// 导出单例实例
export const audioManager = AudioManager.getInstance();
